/** @jsx jsx */

/** @jsxFrag React.Fragment */
import type { SerializedStyles } from '@emotion/react';
import { jsx } from '@emotion/react';

import * as React from 'react';

import { ChatMessage as CdsLabsChatMessage } from '@coursera-web/cds-labs';

import { useTracker } from '@coursera/event-pulse/react';

import type { FeedbackIssueOption } from 'bundles/ai-coach-platform/components/patterns/chat/FeedbackModal';
import ChatMessageBody from 'bundles/ai-coach-platform/components/patterns/chat/common/messages/ChatMessageBody';
import useFeedback from 'bundles/ai-coach-platform/components/patterns/chat/hooks/useFeedback';
import ChatRecommendations from 'bundles/ai-coach-platform/components/patterns/chat/recommendations/ChatRecommendations';
import type { MessageTypes } from 'bundles/ai-coach-platform/components/patterns/chat/store/types';
import type { CoachPersona, SendMessageFunc } from 'bundles/ai-coach-platform/components/patterns/chat/types';
import {
  isCoachMessage as isCoachMessageUtil,
  isErrorMessage as isErrorMessageUtil,
  isTextMessage as isTextMessageUtil,
} from 'bundles/ai-coach-platform/components/patterns/chat/utils/messageUtils';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';

export type Props = React.PropsWithChildren<{
  /**
   * current chat message
   */
  message: MessageTypes;
  /**
   * zero-based index of the current message
   */
  index: number;
  /**
   * all chat messages in store currently
   */
  messages: MessageTypes[];
  sendMessage: SendMessageFunc;
  learnerPersona: CoachPersona;
  coachPersona: CoachPersona;

  hideRecommendedActions?: boolean;
  /**
   * Hides all message actions (copy button and feedback) for this message.
   * Used e.g. for dialogue intro messages shown before the learner has started the conversation.
   */
  hideMessageActions?: boolean;
  /**
   * Hides only the copy button while keeping feedback actions.
   * Used e.g. for graded role plays, where copying is blocked to discourage cheating.
   */
  hideCopyButton?: boolean;
  searchTerm?: string;
  recommendedActionsLabel?: string;
  onRecommendedActionClick?: () => void;
  /**
   * Optional caller-supplied styles applied on top of recommended action button's default styles.
   * Temporary prop for the Two-Panel XDP experiment (MERCH-2963). Please consult the platform team before using it in your project.
   */
  recommendedActionButtonCustomStyles?: SerializedStyles;
  feedbackIssueOptions?: FeedbackIssueOption[];
  onFeedbackSubmitted?: (args: { messageId: string; userIssue?: string; userComments: string }) => void;
}>;

const ChatMessage = ({
  message,
  index,
  messages,
  sendMessage,
  coachPersona,
  learnerPersona,
  children,
  hideRecommendedActions,
  hideMessageActions,
  hideCopyButton,
  searchTerm,
  recommendedActionsLabel,
  onRecommendedActionClick,
  recommendedActionButtonCustomStyles,
  feedbackIssueOptions,
  onFeedbackSubmitted,
}: Props) => {
  const { sender } = message;

  const isLastMessage = index === messages.length - 1;
  const isCoachMessage = isCoachMessageUtil(sender);

  const showCopyMessage = isCoachMessage && isTextMessageUtil(message as MessageTypes) && !hideCopyButton;
  const isErrorMessage = isCoachMessage && isErrorMessageUtil(message as MessageTypes);

  const { onThumbsUp, onThumbsDown, thumbsUpSelected, thumbsDownSelected, feedbackModal } = useFeedback({
    message,
    feedbackIssueOptions,
    onFeedbackSubmitted,
  });

  const track = useTracker();
  const trackingContext = useCoachTrackingContext()?.data;

  const handleCopy = React.useCallback<() => void>(() => {
    if (!trackingContext) return;
    track('interact_coach', {
      coachInteractionType: 'copy_coach_message',
      coach: { mode: trackingContext.mode },
      ...(trackingContext.courseId && { product: { type: 'course', id: trackingContext.courseId as string } }),
      ...(trackingContext.itemId && {
        courseItem: {
          id: trackingContext.itemId as string,
          ...(trackingContext.itemType && { type: trackingContext.itemType }),
        },
      }),
    });

    // Coach eventing consolidation [COACH-2404]: dual-emit onto the new ClickCoachElement schema
    track('click_coach_element', {
      clickElementType: 'copy_message',
      coach: { mode: trackingContext.mode },
      ...(trackingContext.courseId && { product: { type: 'course', id: trackingContext.courseId as string } }),
      ...(trackingContext.itemId && {
        courseItem: {
          id: trackingContext.itemId as string,
          ...(trackingContext.itemType && { type: trackingContext.itemType }),
        },
      }),
    });
  }, [track, trackingContext]);

  const recommendations =
    isCoachMessage && !hideRecommendedActions ? (
      <ChatRecommendations
        message={message}
        sendMessage={sendMessage}
        showRecommendedActions={isLastMessage}
        recommendedActionsLabel={recommendedActionsLabel}
        onRecommendedActionClick={onRecommendedActionClick}
        recommendedActionButtonCustomStyles={recommendedActionButtonCustomStyles}
      />
    ) : null;

  if (isCoachMessage) {
    return (
      <CdsLabsChatMessage
        variant="llm"
        content={
          <ChatMessageBody message={message} searchTerm={searchTerm}>
            {recommendations}
            {children}
          </ChatMessageBody>
        }
        isLatestMessage={isLastMessage}
        actionButtons={
          !isErrorMessage && !hideMessageActions
            ? {
                showCopy: showCopyMessage,
                onCopy: handleCopy,
                onThumbsUp,
                thumbsUpSelected,
                onThumbsDown,
                thumbsDownSelected,
                extraActions: feedbackModal,
              }
            : undefined
        }
      />
    );
  }

  return <CdsLabsChatMessage variant="user" content={message.text} />;
};

export default ChatMessage;
